import { useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Search,
  FileText,
  Pill,
  Leaf,
  Droplet,
  Sparkles,
  Baby,
  Stethoscope,
  HeartPulse,
  Flower2,
  SearchX,
  ArrowUpRight,
} from "lucide-react";
import { Input } from "@/components/ui/input";
import { CATEGORIES, whatsappHref } from "@/data/site";
import { CATEGORY } from "@/constants/testIds";
import { fadeUp, viewport } from "@/lib/motion";

const ICONS = {
  FileText,
  Pill,
  Leaf,
  Droplet,
  Sparkles,
  Baby,
  Stethoscope,
  HeartPulse,
  Flower2,
};

export const Categories = () => {
  const [query, setQuery] = useState("");

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return CATEGORIES;
    return CATEGORIES.filter(
      (c) =>
        c.title.toLowerCase().includes(q) ||
        c.desc.toLowerCase().includes(q) ||
        c.tags.some((t) => t.includes(q))
    );
  }, [query]);

  return (
    <section id="categories" className="relative bg-surface py-24 lg:py-32">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-8">
          <div className="max-w-2xl">
            <motion.p
              initial="hidden"
              whileInView="show"
              viewport={viewport}
              variants={fadeUp}
              className="font-serif-accent italic text-lg text-brand-green"
            >
              What we stock
            </motion.p>
            <motion.h2
              initial="hidden"
              whileInView="show"
              viewport={viewport}
              variants={fadeUp}
              className="mt-3 font-heading font-bold tracking-tight text-ink text-3xl sm:text-4xl lg:text-5xl leading-tight"
            >
              Medicine categories for every need.
            </motion.h2>
          </div>

          <motion.div
            initial="hidden"
            whileInView="show"
            viewport={viewport}
            variants={fadeUp}
            className="relative w-full lg:w-80"
          >
            <Search className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400" />
            <Input
              data-testid={CATEGORY.searchInput}
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search medicines & care…"
              className="h-14 rounded-full border-slate-200 bg-white pl-12 pr-4 text-base shadow-sm focus-visible:ring-2 focus-visible:ring-brand-blue/25 focus-visible:border-brand-blue"
            />
          </motion.div>
        </div>

        <div className="mt-14 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          <AnimatePresence mode="popLayout">
            {filtered.map((c) => {
              const Icon = ICONS[c.icon] || Pill;
              return (
                <motion.a
                  key={c.slug}
                  href={whatsappHref}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid={CATEGORY.card(c.slug)}
                  layout
                  initial={{ opacity: 0, y: 24 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, scale: 0.94 }}
                  transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
                  whileHover={{ y: -6 }}
                  className="group relative overflow-hidden rounded-3xl border border-slate-100 bg-white p-8 shadow-[0_8px_30px_rgb(0,0,0,0.04)] transition-shadow hover:shadow-[0_20px_40px_rgb(0,0,0,0.08)]"
                >
                  <div className="absolute -right-10 -top-10 h-32 w-32 rounded-full bg-brand-green/5 transition-transform duration-500 group-hover:scale-150" />
                  <div className="relative flex items-start justify-between">
                    <span className="grid place-items-center h-14 w-14 rounded-2xl bg-brand-blue/10 text-brand-blue transition-colors group-hover:bg-brand-blue group-hover:text-white">
                      <Icon className="h-6 w-6" strokeWidth={2} />
                    </span>
                    <ArrowUpRight className="h-5 w-5 text-slate-300 transition-all group-hover:text-brand-green group-hover:rotate-45" />
                  </div>
                  <h3 className="relative mt-6 font-heading text-xl font-semibold text-ink">
                    {c.title}
                  </h3>
                  <p className="relative mt-2 text-sm text-slate-600 leading-relaxed">
                    {c.desc}
                  </p>
                </motion.a>
              );
            })}
          </AnimatePresence>
        </div>

        {filtered.length === 0 && (
          <div
            data-testid={CATEGORY.empty}
            className="mt-14 flex flex-col items-center justify-center rounded-3xl border border-dashed border-slate-300 bg-white py-16 text-center"
          >
            <SearchX className="h-10 w-10 text-slate-300" />
            <p className="mt-4 font-heading text-lg font-semibold text-ink">
              No matches for “{query}”
            </p>
            <p className="mt-1 text-sm text-slate-500">
              Ask us on WhatsApp — we likely have it in store.
            </p>
          </div>
        )}
      </div>
    </section>
  );
};
